import React from 'react'
import withStyles, { WithStyles } from '@material-ui/core/styles/withStyles'
import {
  Select,
  SelectProps,
  MenuItem,
  FormControl,
  FormLabel,
} from '@material-ui/core'
import clsx from 'clsx'

import { Intent } from 'common/models/typings/Intent'

import FormInputHelper, { HelperVariant } from '../InputHelper'

import SelectInput from './SelectInput'
import { styles } from './useStyles'

export interface SelectMenuProps extends WithStyles<typeof styles> {
  disabled?: boolean
  success?: boolean
  warning?: boolean
  error?: boolean
  value?: unknown
  icon?: React.ReactElement | undefined
  size?: boolean
  helperIcon?: Intent
  helperVariant?: HelperVariant
  placeholder?: string
  label?: string
  onFocus?: SelectProps['onFocus']
  onBlur?: SelectProps['onBlur']
  onChange?: SelectProps['onChange']
  helperText?: string
  fullWidth?: SelectProps['fullWidth']
  children?: React.ReactNode
}

export interface SelectMenuState {
  fieldValue: unknown
}

const placeholderRecogValue = 'none'

class SelectMenuClass extends React.PureComponent<
  SelectMenuProps,
  SelectMenuState
> {
  public static getDerivedStateFromProps(
    props: SelectMenuProps,
  ): Partial<SelectMenuState> | null {
    const { value } = props

    const isPlaceholderValue = value === undefined || value === null
    return {
      fieldValue: isPlaceholderValue ? placeholderRecogValue : value,
    }
  }

  public constructor(props: SelectMenuProps) {
    super(props)

    this.state = {
      fieldValue: placeholderRecogValue,
    }
  }

  private get selectClasses(): string {
    const {
      props: { classes, size, fullWidth },
      state: { fieldValue },
    } = this

    return clsx(
      classes.select,
      size && classes.selectMedium,
      fieldValue === placeholderRecogValue
        ? classes.placeholderTextColor
        : null,
      fullWidth && classes.selectFullWidth,
    )
  }

  private get iconElement(): React.ReactElement | undefined {
    const { icon, classes } = this.props

    return icon
      ? React.cloneElement(icon, {
          ...icon.props,
          className: clsx(classes.icon, icon.props.className),
        })
      : undefined
  }

  private get inputClasses(): string {
    const { classes, warning, disabled, success, error } = this.props

    return clsx({
      [classes.disabled]: disabled,
      [classes.warning]: warning,
      [classes.success]: success,
      [classes.error]: error,
    })
  }

  public render(): JSX.Element | null {
    const {
      props: {
        disabled,
        classes,
        label,
        placeholder,
        helperIcon,
        helperVariant,
        helperText,
        children,
        onBlur,
        onFocus,
        onChange,
        fullWidth,
      },
      state: { fieldValue: value },
    } = this

    return (
      <FormControl className={classes.form}>
        <FormLabel className={classes.label}>{label}</FormLabel>
        <Select
          value={value}
          classes={{
            select: this.selectClasses,
            icon: classes.dropDownIcon,
          }}
          MenuProps={{ classes: { paper: classes.paper, list: classes.list } }}
          input={
            <SelectInput
              disabled={disabled}
              classes={{ root: this.inputClasses }}
              startAdornment={this.iconElement}
              fullWidth={fullWidth}
            />
          }
          onBlur={onBlur}
          onFocus={onFocus}
          onChange={onChange}
          fullWidth={fullWidth}
        >
          {placeholder ? (
            <MenuItem
              disabled
              className={classes.placeholderHidden}
              value={placeholderRecogValue}
            >
              {placeholder}
            </MenuItem>
          ) : null}

          {children}
        </Select>
        {helperText && (
          <FormInputHelper
            icon={helperIcon}
            variant={helperVariant}
            text={helperText}
          />
        )}
      </FormControl>
    )
  }
}

export default withStyles(styles)(SelectMenuClass)
